import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  Popover,
  Divider,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Pets as PetsIcon,
  Egg as EggIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  AttachMoney as MoneyIcon,
  Restaurant as FeedIcon,
  Assessment as AssessmentIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  Assignment as RequestIcon,
  LocalShipping as ProcurementIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Warning as WarningIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  MedicalServices as MedicalServicesIcon,
  Inventory as InventoryIcon,
  Factory as FactoryIcon,
  Inbox as CrateIcon,
  SyncAlt as SyncAltIcon,
  SwapHoriz as FeedSwitchIcon,
  Vaccines as VaccinesIcon,
  CalendarMonth as CalendarMonthIcon,
  AutoFixHigh as AutoFixHighIcon,
  NotificationsActive as PHAlertIcon,
  BarChart as PHReportIcon,
  AccountBalance as AccountBalanceIcon,
  ShowChart as FCRIcon,
  Timeline as TimelineIcon,
  AccountBalanceWallet as CashFlowIcon,
  EventAvailable as AttendanceIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAlerts, useAlertCount } from '../../hooks/useAlerts';
import Logo from '../../assets/image.jpg';

const drawerWidth = 260;

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  {
    text: 'Birds',
    icon: <PetsIcon />,
    path: '/birds',
    children: [
      { text: 'Birds', icon: <PetsIcon />, path: '/birds' },
      { text: 'Bird Weights', icon: <AssessmentIcon />, path: '/bird-weights' },
      { text: 'Weight Submissions', icon: <CrateIcon />, path: '/weight-submissions' },
      { text: 'Flock Weight Trend', icon: <TimelineIcon />, path: '/weight-trend' },
      { text: 'Procurements', icon: <ProcurementIcon />, path: '/procurements' },
      { text: 'Vaccination Alerts', icon: <MedicalServicesIcon />, path: '/vaccination-alerts' },
      {
        text: 'Vaccination Schedule',
        icon: <MedicalServicesIcon />,
        path: '/vaccination-schedule',
      },
    ],
  },
  {
    text: 'Egg Management',
    icon: <EggIcon />,
    path: '/eggs',
    children: [
      { text: 'Production', icon: <EggIcon />, path: '/eggs/production' },
      { text: 'Bird Production', icon: <EggIcon />, path: '/eggs/production/bird' },
      { text: 'Prod vs Sales', icon: <AssessmentIcon />, path: '/eggs/correlation' },
      { text: 'Production Curve', icon: <TimelineIcon />, path: '/eggs/production-curve' },
      { text: 'Batch Comparison', icon: <AssessmentIcon />, path: '/eggs/comparison' },
    ],
  },
  {
    text: 'Feed Management',
    icon: <FeedIcon />,
    path: '/feed',
    children: [
      { text: 'Stock Levels', icon: <AssessmentIcon />, path: '/feed/stock' },
      { text: 'Procurements', icon: <ProcurementIcon />, path: '/feed/procurements' },
      { text: 'Batch Assignments', icon: <RequestIcon />, path: '/feed/assignments' },
      { text: 'Calculator', icon: <AssessmentIcon />, path: '/feed/calculator' },
      { text: 'Raw Materials', icon: <InventoryIcon />, path: '/feed/raw-materials' },
      { text: 'Feed Production', icon: <FactoryIcon />, path: '/feed/production' },
      { text: 'Production Templates', icon: <AssessmentIcon />, path: '/feed/templates' },
      { text: 'Price Impact', icon: <AssessmentIcon />, path: '/feed/price-impact' },
      {
        text: 'Procurement Planner',
        icon: <ShoppingCartIcon />,
        path: '/feed/procurement-planner',
      },
      { text: 'Transition Tracker', icon: <SyncAltIcon />, path: '/feed/transition-tracker' },
      { text: 'Feed Switch', icon: <FeedSwitchIcon />, path: '/feed/switch' },
      { text: 'FCR Dashboard', icon: <FCRIcon />, path: '/feed/fcr' },
    ],
  },
  {
    text: 'Financial',
    icon: <MoneyIcon />,
    path: '/financial',
    children: [
      { text: 'Egg Sales', icon: <ShoppingCartIcon />, path: '/financial/sales' },
      { text: 'Prices', icon: <MoneyIcon />, path: '/prices' },
      { text: 'Pricing Suggestions', icon: <AssessmentIcon />, path: '/pricing-suggestions' },
      { text: 'Cycles', icon: <AssessmentIcon />, path: '/financial/cycles' },
      { text: 'Cycle Performance', icon: <AssessmentIcon />, path: '/financial/cycle-performance' },
      { text: 'Expenditures', icon: <MoneyIcon />, path: '/financial/expenditures' },
      {
        text: 'Capital Investments',
        icon: <AccountBalanceIcon />,
        path: '/capital-investments',
      },
      { text: 'Suppliers & Payments', icon: <ProcurementIcon />, path: '/financial/suppliers' },
      { text: 'Cash Flow', icon: <CashFlowIcon />, path: '/financial/cash-flow' },
      { text: 'Financial Summary', icon: <PHReportIcon />, path: '/financial/summary' },
    ],
  },
  { text: 'Crate Stocks', icon: <CrateIcon />, path: '/crate-stocks' },
  { text: 'Bird Logs', icon: <AssessmentIcon />, path: '/bird-logs' },
  { text: 'Procurement Assignments', icon: <PersonAddIcon />, path: '/bird-procurement-assignees' },
  {
    text: 'Customers',
    icon: <PeopleIcon />,
    path: '/customers',
    children: [
      { text: 'Customer List', icon: <PeopleIcon />, path: '/customers' },
      { text: 'SMS Receipt Log', icon: <SmsIcon />, path: '/customers/sms-logs' },
    ],
  },
  { text: 'Employees', icon: <PeopleIcon />, path: '/employees' },
  { text: 'Batch Employees', icon: <PersonAddIcon />, path: '/batch-employee-assignments' },
  { text: 'Attendance', icon: <AttendanceIcon />, path: '/attendance' },
  { text: 'Mortalities', icon: <WarningIcon />, path: '/mortalities' },
  { text: 'Requests', icon: <RequestIcon />, path: '/requests' },
  { text: 'Vet Instructions', icon: <MedicalServicesIcon />, path: '/vet-instructions' },
  { text: 'Vet Visit Reports', icon: <MedicalServicesIcon />, path: '/vet-visit-reports' },
  { text: 'Medications', icon: <MedicalServicesIcon />, path: '/medications' },
  {
    text: 'Preventive Health',
    icon: <VaccinesIcon />,
    path: '/preventive-health',
    children: [
      { text: 'Calendar', icon: <CalendarMonthIcon />, path: '/preventive-health/calendar' },
      {
        text: 'Programme Editor',
        icon: <AutoFixHighIcon />,
        path: '/preventive-health/programmes',
      },
      { text: 'Alerts', icon: <PHAlertIcon />, path: '/preventive-health/alerts' },
      { text: 'Reports', icon: <PHReportIcon />, path: '/preventive-health/reports' },
    ],
  },
  { text: 'User Permissions', icon: <SecurityIcon />, path: '/security/permissions' },
];

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);

  // Fetch alerts
  const { data: alerts = [] } = useAlerts();
  const { data: alertCount = 0 } = useAlertCount();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const notificationOpen = Boolean(notificationAnchorEl);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleExpand = (text: string) => {
    setExpandedItems((prev) =>
      prev.includes(text) ? prev.filter((item) => item !== text) : [...prev, text]
    );
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background:
          'linear-gradient(180deg, rgba(131, 65, 5, 0.03) 0%, rgba(255, 152, 0, 0.02) 100%)',
        borderRight: '1px solid rgba(131, 65, 5, 0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 2.5,
          background:
            'linear-gradient(135deg, rgba(131, 65, 5, 0.08) 0%, rgba(255, 152, 0, 0.08) 100%)',
          borderBottom: '1px solid rgba(131, 65, 5, 0.1)',
          minHeight: 64,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 2,
              boxShadow: '0px 4px 12px rgba(131, 65, 5, 0.3)',
            }}
          >
            <Avatar src={Logo} alt="Narnia Farms Logo" style={{ width: '24px', height: '24px' }} />
          </Box>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              letterSpacing: '-0.5px',
            }}
          >
            Narnia Farms
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      <List sx={{ px: 1.5, py: 2 }}>
        {navItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={
                  location.pathname === item.path ||
                  (location.pathname.startsWith(item.path) &&
                    item.path !== '/' &&
                    !item.children) ||
                  (item.children &&
                    item.children.some((child) => location.pathname.startsWith(child.path)))
                }
                onClick={() => {
                  if (item.children) {
                    toggleExpand(item.text);
                  } else {
                    handleNavigation(item.path);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  '&.Mui-selected': {
                    background:
                      'linear-gradient(135deg, rgba(131, 65, 5, 0.12) 0%, rgba(255, 152, 0, 0.12) 100%)',
                    borderLeft: '3px solid',
                    borderLeftColor: 'primary.main',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, rgba(131, 65, 5, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
                    },
                  },
                  '&:hover': {
                    background: 'rgba(131, 65, 5, 0.05)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      location.pathname === item.path ||
                      (location.pathname.startsWith(item.path) &&
                        item.path !== '/' &&
                        !item.children) ||
                      (item.children &&
                        item.children.some((child) => location.pathname.startsWith(child.path)))
                        ? 'primary.main'
                        : 'text.secondary',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight:
                      location.pathname === item.path ||
                      (location.pathname.startsWith(item.path) &&
                        item.path !== '/' &&
                        !item.children) ||
                      (item.children &&
                        item.children.some((child) => location.pathname.startsWith(child.path)))
                        ? 600
                        : 400,
                    fontSize: '0.9rem',
                  }}
                />
                {item.children &&
                  (expandedItems.includes(item.text) ? (
                    <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  ) : (
                    <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  ))}
              </ListItemButton>
            </ListItem>
            {item.children && expandedItems.includes(item.text) && (
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {item.children.map((child) => (
                  <ListItemButton
                    key={child.text}
                    sx={{
                      pl: 4,
                      py: 1,
                      mb: 0.5,
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&.Mui-selected': {
                        background:
                          'linear-gradient(135deg, rgba(131, 65, 5, 0.08) 0%, rgba(255, 152, 0, 0.08) 100%)',
                        borderLeft: '2px solid',
                        borderLeftColor: 'secondary.main',
                      },
                      '&:hover': {
                        background: 'rgba(131, 65, 5, 0.04)',
                        transform: 'translateX(4px)',
                      },
                    }}
                    selected={
                      location.pathname === child.path || location.pathname.startsWith(child.path)
                    }
                    onClick={() => handleNavigation(child.path)}
                  >
                    <ListItemIcon
                      sx={{
                        color:
                          location.pathname === child.path ||
                          location.pathname.startsWith(child.path)
                            ? 'secondary.main'
                            : 'text.secondary',
                        minWidth: 36,
                      }}
                    >
                      {child.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={child.text}
                      primaryTypographyProps={{
                        fontWeight:
                          location.pathname === child.path ||
                          location.pathname.startsWith(child.path)
                            ? 600
                            : 400,
                        fontSize: '0.85rem',
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: desktopOpen ? `${drawerWidth}px` : 0 },
          background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 152, 0, 0.3)',
          boxShadow: '0px 2px 8px rgba(255, 152, 0, 0.1)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, md: 70 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon sx={{ color: '#ff9800' }} />
          </IconButton>
          <IconButton
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDesktopDrawerToggle}
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              color: '#ff9800',
              '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.08)' },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                color: '#ffffff',
                letterSpacing: '-0.5px',
              }}
            >
              {(() => {
                const currentItem = navItems.find((item) => {
                  if (item.path === location.pathname) return true;
                  if (item.children) {
                    return item.children.some(
                      (child) =>
                        location.pathname.startsWith(child.path) ||
                        location.pathname.startsWith(item.path)
                    );
                  }
                  return location.pathname.startsWith(item.path) && item.path !== '/';
                });

                if (currentItem?.children) {
                  const childItem = currentItem.children.find((child) =>
                    location.pathname.startsWith(child.path)
                  );
                  return childItem?.text || currentItem.text;
                }

                return currentItem?.text || 'Dashboard';
              })()}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Notification Icon */}
            <IconButton onClick={handleNotificationClick} size="large">
              <Badge badgeContent={alertCount} color="error">
                <NotificationsIcon sx={{ color: '#ff9800' }} />
              </Badge>
            </IconButton>

            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#ffffff' }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {user?.role || 'Administrator'}
              </Typography>
            </Box>
            <IconButton onClick={handleMenuClick} size="small">
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #834105 0%, #ff9800 100%)',
                  fontWeight: 700,
                  boxShadow: '0px 2px 8px rgba(131, 65, 5, 0.3)',
                }}
              >
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  borderRadius: 2,
                  minWidth: 200,
                  boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.6)',
                  border: '1px solid rgba(255, 152, 0, 0.2)',
                },
              }}
            >
              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  '&:hover': {
                    background: 'rgba(211, 47, 47, 0.08)',
                  },
                }}
              >
                <LogoutIcon sx={{ mr: 1.5, color: 'error.main' }} />
                <Typography variant="body2" fontWeight={500} color="error.main">
                  Logout
                </Typography>
              </MenuItem>
            </Menu>

            {/* Notification Popover */}
            <Popover
              open={notificationOpen}
              anchorEl={notificationAnchorEl}
              onClose={handleNotificationClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  borderRadius: 2,
                  width: 400,
                  maxHeight: 600,
                  boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.7)',
                  border: '1px solid rgba(255, 152, 0, 0.25)',
                },
              }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 152, 0, 0.15)' }}>
                <Typography variant="h6" fontWeight={700}>
                  Notifications
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {alertCount} {alertCount === 1 ? 'alert' : 'alerts'}
                </Typography>
              </Box>
              <List sx={{ p: 0, maxHeight: 500, overflow: 'auto' }}>
                {alerts.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No notifications
                    </Typography>
                  </Box>
                ) : (
                  alerts.map((alert: any) => (
                    <React.Fragment key={alert.id}>
                      <ListItem
                        sx={{
                          py: 2,
                          px: 2,
                          '&:hover': {
                            bgcolor: 'rgba(131, 65, 5, 0.04)',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor:
                                alert.severity === 'critical'
                                  ? 'rgba(211, 47, 47, 0.1)'
                                  : alert.severity === 'warning'
                                    ? 'rgba(255, 152, 0, 0.1)'
                                    : 'rgba(33, 150, 243, 0.1)',
                            }}
                          >
                            <WarningIcon
                              sx={{
                                fontSize: 20,
                                color:
                                  alert.severity === 'critical'
                                    ? '#d32f2f'
                                    : alert.severity === 'warning'
                                      ? '#ff9800'
                                      : '#2196f3',
                              }}
                            />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight={600}>
                                {alert.title}
                              </Typography>
                              <Chip
                                label={alert.type}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: 'rgba(131, 65, 5, 0.08)',
                                  color: '#834105',
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {alert.message}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {new Date(alert.created_at).toLocaleString()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))
                )}
              </List>
            </Popover>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: desktopOpen ? drawerWidth : 0 },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="persistent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
              transition: theme.transitions.create('transform', {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            },
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          minHeight: '100vh',
          background: '#0a0a0a',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, md: 70 } }} />
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
